import { useState, useRef, useEffect } from "react";
import { useParams, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useGetProject } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { usePageTitle } from "@/hooks/use-page-title";
import { useToast } from "@/hooks/use-toast";
import { uploadFileAndGetUrl } from "@/lib/upload";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Camera, Clock, MapPin, Share2, Download, Loader2, Upload, X, Wrench, Receipt,
} from "lucide-react";
import { ReceiptImport } from "@/components/receipt-import";
import { getTrialInfo } from "@/hooks/use-auth";
import { ProTeaserSection } from "@/components/pro-gate";
import FieldPhotosTab from "./photos-tab";
import FieldLaborTab from "./labor-tab";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export const PHOTOS_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "photos"];
export const SUMMARY_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "photos", "summary"];
const LABOR_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "labor"];
const OPEN_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "labor", "open"];

type LaborEntry = {
  id: number;
  projectId: number;
  userId: number;
  startTime: string;
  endTime: string | null;
  durationMinutes: number | null;
  hourlyRate: number | null;
  notes: string | null;
  workerName: string;
  createdAt: string;
};

type PendingPhoto = { file: File; preview: string };

function formatDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowLocal(): string {
  return formatDatetimeLocal(new Date().toISOString());
}

function FieldCostsTab({ projectId, user }: { projectId: number; user: ReturnType<typeof useAuth>["user"] }) {
  const { hasProAccess } = getTrialInfo(user);
  if (!hasProAccess) {
    return (
      <ProTeaserSection
        title="Receipt Capture"
        description="Photograph receipts on-site and record actual material costs against your project quotes."
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {[["Home Depot", "$284.50"], ["Pro Fasteners", "$91.20"], ["City Lumber", "$612.00"]].map(([vendor, total]) => (
            <div key={vendor} style={{ padding: "0.75rem 1rem", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem", backgroundColor: "hsl(var(--card))", display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{vendor}</div>
                <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Materials · 3 items</div>
              </div>
              <div style={{ fontWeight: 700 }}>{total}</div>
            </div>
          ))}
        </div>
      </ProTeaserSection>
    );
  }
  return (
    <div>
      <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", marginBottom: "1rem" }}>
        Capture a receipt to record actual material costs against this project.
      </p>
      <ReceiptImport projectId={projectId} />
    </div>
  );
}

export default function FieldProjectPage() {
  const { id } = useParams();
  const projectId = parseInt(id ?? "0", 10);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("photos");

  const projectQuery = useGetProject(projectId);
  const project = projectQuery.data;
  const isField = user?.role === "field";

  usePageTitle(project?.name ?? "Field");

  // ── Open-entry query (drives the Clock In / Clock Out button in the action bar) ──
  const openQuery = useQuery<LaborEntry | null>({
    queryKey: OPEN_QUERY_KEY(projectId),
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/labor/open`);
      if (!res.ok) throw new Error();
      return res.json();
    },
    enabled: isField,
  });
  const openEntry = openQuery.data ?? null;

  // ── Clock-In dialog ──
  const [clockInOpen, setClockInOpen] = useState(false);
  const [clockInTime, setClockInTime] = useState(nowLocal());
  const [clockInNotes, setClockInNotes] = useState("");

  const clockInMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/labor/clock-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          startTime: new Date(clockInTime).toISOString(),
          notes: clockInNotes || null,
        }),
      });
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Clocked in" });
      queryClient.invalidateQueries({ queryKey: LABOR_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: OPEN_QUERY_KEY(projectId) });
      setClockInOpen(false);
      setClockInNotes("");
    },
    onError: () => toast({ title: "Failed to clock in", variant: "destructive" }),
  });

  // ── Clock-Out dialog ──
  const [clockOutOpen, setClockOutOpen] = useState(false);
  const [clockOutTime, setClockOutTime] = useState(nowLocal());
  const [clockOutNotes, setClockOutNotes] = useState("");

  const clockOutMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${BASE}/api/field/projects/${projectId}/labor/${openEntry!.id}/clock-out`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            endTime: new Date(clockOutTime).toISOString(),
            notes: clockOutNotes || null,
          }),
        },
      );
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Clocked out" });
      queryClient.invalidateQueries({ queryKey: LABOR_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: OPEN_QUERY_KEY(projectId) });
      setClockOutOpen(false);
      setClockOutNotes("");
    },
    onError: () => toast({ title: "Failed to clock out", variant: "destructive" }),
  });

  // ── Multi-photo capture ──
  const [captureOpen, setCaptureOpen] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [captureArea, setCaptureArea] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // GPS state — acquired once when the capture dialog opens; attached to all
  // photos in the batch. Non-blocking: photos upload fine without it.
  type GpsStatus = "idle" | "acquiring" | "ready" | "denied" | "unavailable";
  const [gpsStatus, setGpsStatus] = useState<GpsStatus>("idle");
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);

  const acquireGps = () => {
    if (!navigator.geolocation) { setGpsStatus("unavailable"); return; }
    setGpsStatus("acquiring");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsStatus("ready");
      },
      () => {
        setGpsStatus("denied");
        setGpsCoords(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPendingPhotos((prev) => [...prev, ...next]);
  };

  const removePhoto = (index: number) => {
    setPendingPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleCaptureClose = () => {
    if (isUploading) return;
    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPendingPhotos([]);
    setCaptureArea("");
    setCaptureOpen(false);
    setGpsStatus("idle");
    setGpsCoords(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleUploadAll = async () => {
    if (pendingPhotos.length === 0) return;
    setIsUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const photo of pendingPhotos) {
      try {
        const url = await uploadFileAndGetUrl(photo.file);
        const res = await fetch(`${BASE}/api/field/projects/${projectId}/photos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url,
            area: captureArea || null,
            takenAt: new Date().toISOString(),
            latitude: gpsCoords?.lat ?? null,
            longitude: gpsCoords?.lng ?? null,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `Server error ${res.status}`);
        }
        successCount++;
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      queryClient.invalidateQueries({ queryKey: PHOTOS_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY(projectId) });
      toast({ title: `${successCount} photo${successCount > 1 ? "s" : ""} added` });
    }
    if (failCount > 0) {
      toast({
        title: `${failCount} photo${failCount > 1 ? "s" : ""} failed to upload`,
        variant: "destructive",
      });
    }

    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPendingPhotos([]);
    setCaptureArea("");
    setCaptureOpen(false);
    setIsUploading(false);
    setGpsStatus("idle");
    setGpsCoords(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  // ── Owner-only actions ──
  const handleShare = async () => {
    try {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/share`, {
        method: "POST",
      });
      if (!res.ok) throw new Error();
      const { shareUrl } = await res.json();
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: "Share link copied to clipboard" });
    } catch {
      toast({ title: "Failed to generate share link", variant: "destructive" });
    }
  };

  const handleDownloadPdf = () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    window.open(`${BASE}/api/field/projects/${projectId}/report-pdf?tz=${encodeURIComponent(tz)}`, "_blank");
  };

  if (projectQuery.isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
        <Loader2
          style={{ width: "2rem", height: "2rem" }}
          className="animate-spin text-muted-foreground"
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          color: "hsl(var(--muted-foreground))",
        }}
      >
        Project not found.
      </div>
    );
  }

  return (
    <div>
      {/* Back + header */}
      <div style={{ marginBottom: "1rem" }}>
        <Link href="/field">
          <button
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
              fontSize: "0.875rem",
              color: "hsl(var(--muted-foreground))",
              marginBottom: "0.75rem",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            className="hover:text-foreground transition-colors"
          >
            <ArrowLeft style={{ width: "1rem", height: "1rem" }} />
            Field
          </button>
        </Link>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>
              {project.name}
            </h1>
            {(project as { address?: string }).address && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.375rem",
                  fontSize: "0.875rem",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                <MapPin style={{ width: "0.875rem", height: "0.875rem" }} />
                {(project as { address?: string }).address}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {!isField && (
              <>
                <Button variant="outline" size="sm" onClick={handleShare}>
                  <Share2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
                  Share
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
                  <Download
                    style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }}
                  />
                  Export PDF
                </Button>
              </>
            )}
            <Link href="/field/tools">
              <Button size="sm">
                <Wrench style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
                Tools
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Field-worker persistent action bar — visible on both tabs */}
      {isField && (
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            marginBottom: "1rem",
            padding: "0.75rem",
            backgroundColor: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          {openEntry ? (
            <Button
              onClick={() => {
                setClockOutTime(nowLocal());
                setClockOutOpen(true);
              }}
              variant="outline"
              className="flex-1 h-12 md:h-9 text-base md:text-sm"
              style={{
                borderColor: "hsl(var(--destructive))",
                color: "hsl(var(--destructive))",
              }}
            >
              <Clock style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
              Clock Out
            </Button>
          ) : (
            <Button
              onClick={() => {
                setClockInTime(nowLocal());
                setClockInOpen(true);
              }}
              className="flex-1 h-12 md:h-9 text-base md:text-sm"
            >
              <Clock style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
              Clock In
            </Button>
          )}
          <Button
            onClick={() => { setCaptureOpen(true); acquireGps(); }}
            variant="secondary"
            className="flex-1 h-12 md:h-9 text-base md:text-sm"
          >
            <Camera style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
            Capture
          </Button>
          <Link href="/field/tools" style={{ flex: 1 }}>
            <Button
              variant="outline"
              className="w-full h-12 md:h-9 text-base md:text-sm"
            >
              <Wrench style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
              Tools
            </Button>
          </Link>
        </div>
      )}

      {/* Tabs — full width to match app chrome */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full" style={{ marginBottom: "1rem" }}>
          <TabsTrigger
            value="photos"
            className="flex-1"
            style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}
          >
            <Camera style={{ width: "0.875rem", height: "0.875rem" }} />
            Photos
          </TabsTrigger>
          <TabsTrigger
            value="labor"
            className="flex-1"
            style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}
          >
            <Clock style={{ width: "0.875rem", height: "0.875rem" }} />
            Time Log
          </TabsTrigger>
          <TabsTrigger
            value="costs"
            className="flex-1"
            style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}
          >
            <Receipt style={{ width: "0.875rem", height: "0.875rem" }} />
            Costs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="photos">
          <FieldPhotosTab projectId={projectId} />
        </TabsContent>

        <TabsContent value="labor">
          <FieldLaborTab projectId={projectId} />
        </TabsContent>

        <TabsContent value="costs">
          <FieldCostsTab projectId={projectId} user={user} />
        </TabsContent>
      </Tabs>

      {/* ── Clock-In dialog ── */}
      <Dialog open={clockInOpen} onOpenChange={(o) => { if (!o) setClockInOpen(false); }}>
        <DialogContent style={{ maxWidth: "24rem" }}>
          <DialogHeader>
            <DialogTitle>Clock In</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            <div>
              <Label>Start Time</Label>
              <Input
                type="datetime-local"
                value={clockInTime}
                onChange={(e) => setClockInTime(e.target.value)}
                style={{ marginTop: "0.375rem" }}
              />
              <p
                style={{
                  fontSize: "0.75rem",
                  color: "hsl(var(--muted-foreground))",
                  marginTop: "0.25rem",
                }}
              >
                Pre-filled with current time — adjust if needed.
              </p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={clockInNotes}
                onChange={(e) => setClockInNotes(e.target.value)}
                placeholder="Any notes for this shift…"
                style={{ marginTop: "0.375rem", minHeight: "5rem" }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockInOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => clockInMutation.mutate()}
              disabled={clockInMutation.isPending || !clockInTime}
            >
              {clockInMutation.isPending && (
                <Loader2
                  style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }}
                  className="animate-spin"
                />
              )}
              Clock In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Clock-Out dialog ── */}
      <Dialog open={clockOutOpen} onOpenChange={(o) => { if (!o) setClockOutOpen(false); }}>
        <DialogContent style={{ maxWidth: "24rem" }}>
          <DialogHeader>
            <DialogTitle>Clock Out</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            {openEntry && (
              <div
                style={{
                  backgroundColor: "hsl(var(--muted))",
                  borderRadius: "0.5rem",
                  padding: "0.75rem",
                  fontSize: "0.875rem",
                }}
              >
                Clocked in at {new Date(openEntry.startTime).toLocaleString()}
              </div>
            )}
            <div>
              <Label>End Time</Label>
              <Input
                type="datetime-local"
                value={clockOutTime}
                onChange={(e) => setClockOutTime(e.target.value)}
                style={{ marginTop: "0.375rem" }}
              />
              <p
                style={{
                  fontSize: "0.75rem",
                  color: "hsl(var(--muted-foreground))",
                  marginTop: "0.25rem",
                }}
              >
                Pre-filled with current time — adjust if needed.
              </p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={clockOutNotes}
                onChange={(e) => setClockOutNotes(e.target.value)}
                placeholder="Any notes for this shift…"
                style={{ marginTop: "0.375rem", minHeight: "5rem" }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockOutOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => clockOutMutation.mutate()}
              disabled={clockOutMutation.isPending || !clockOutTime}
              style={{
                backgroundColor: "hsl(var(--destructive))",
                color: "hsl(var(--destructive-foreground))",
              }}
            >
              {clockOutMutation.isPending && (
                <Loader2
                  style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }}
                  className="animate-spin"
                />
              )}
              Clock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Multi-photo capture dialog ── */}
      {/* Hidden file inputs — live outside the dialog so they survive dialog unmounts */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          if (cameraInputRef.current) cameraInputRef.current.value = "";
          // On Android/Chrome this re-opens the camera immediately (user gesture
          // chain is still live inside onChange). On iOS this is a no-op; the
          // "Take Another Photo" button below handles that case.
          setTimeout(() => { cameraInputRef.current?.click(); }, 150);
        }}
      />

      <Dialog open={captureOpen} onOpenChange={(open) => { if (!open) handleCaptureClose(); }}>
        <DialogContent style={{ maxWidth: "32rem" }}>
          <DialogHeader>
            <DialogTitle>Add Photos</DialogTitle>
          </DialogHeader>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              padding: "0.25rem 0",
            }}
          >
            {pendingPhotos.length > 0 ? (
              /* Queue view */
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {/* Thumbnail strip */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                    gap: "0.5rem",
                  }}
                >
                  {pendingPhotos.map((photo, index) => (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        aspectRatio: "1",
                        borderRadius: "0.375rem",
                        overflow: "hidden",
                        backgroundColor: "hsl(var(--muted))",
                      }}
                    >
                      <img
                        src={photo.preview}
                        alt={photo.file.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      {!isUploading && (
                        <button
                          onClick={() => removePhoto(index)}
                          style={{
                            position: "absolute",
                            top: "0.25rem",
                            right: "0.25rem",
                            width: "1.25rem",
                            height: "1.25rem",
                            borderRadius: "9999px",
                            backgroundColor: "rgba(0,0,0,0.6)",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                          }}
                        >
                          <X style={{ width: "0.75rem", height: "0.75rem", color: "white" }} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Primary action: take another photo; secondary: choose from library */}
                {!isUploading && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <Button
                      variant="secondary"
                      className="w-full h-12 text-base"
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <Camera style={{ width: "1.1rem", height: "1.1rem", marginRight: "0.5rem" }} />
                      Take Another Photo
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload style={{ width: "0.9rem", height: "0.9rem", marginRight: "0.5rem" }} />
                      Add from Library
                    </Button>
                  </div>
                )}

                <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>
                  {pendingPhotos.length} photo{pendingPhotos.length > 1 ? "s" : ""} queued
                </p>
              </div>
            ) : (
              /* Empty state — choose source */
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    padding: "1.25rem",
                    border: "2px dashed hsl(var(--border))",
                    borderRadius: "0.5rem",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "hsl(var(--foreground))",
                  }}
                  className="hover:border-primary hover:text-primary hover:bg-primary/5"
                >
                  <Upload style={{ width: "1.5rem", height: "1.5rem" }} />
                  Choose from library
                </button>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    padding: "1.25rem",
                    border: "2px dashed hsl(var(--border))",
                    borderRadius: "0.5rem",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "hsl(var(--foreground))",
                  }}
                  className="hover:border-primary hover:text-primary hover:bg-primary/5"
                >
                  <Camera style={{ width: "1.5rem", height: "1.5rem" }} />
                  Take photo
                </button>
              </div>
            )}

            {/* GPS status */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem" }}>
              {gpsStatus === "acquiring" && (
                <>
                  <Loader2 style={{ width: "0.875rem", height: "0.875rem", flexShrink: 0 }} className="animate-spin text-muted-foreground" />
                  <span style={{ color: "hsl(var(--muted-foreground))" }}>Acquiring GPS…</span>
                </>
              )}
              {gpsStatus === "ready" && gpsCoords && (
                <>
                  <span style={{ width: "0.5rem", height: "0.5rem", borderRadius: "9999px", backgroundColor: "#22c55e", flexShrink: 0, display: "inline-block" }} />
                  <a
                    href={`https://maps.google.com/?q=${gpsCoords.lat},${gpsCoords.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "hsl(var(--primary))", textDecoration: "none" }}
                    className="hover:underline"
                  >
                    {gpsCoords.lat.toFixed(5)}, {gpsCoords.lng.toFixed(5)}
                  </a>
                  <span style={{ color: "hsl(var(--muted-foreground))" }}>will be saved with photos</span>
                </>
              )}
              {(gpsStatus === "denied" || gpsStatus === "unavailable") && (
                <>
                  <span style={{ width: "0.5rem", height: "0.5rem", borderRadius: "9999px", backgroundColor: "hsl(var(--muted-foreground))", flexShrink: 0, display: "inline-block" }} />
                  <span style={{ color: "hsl(var(--muted-foreground))" }}>
                    {gpsStatus === "denied" ? "Location access denied — photos will upload without GPS" : "GPS unavailable"}
                  </span>
                </>
              )}
            </div>

            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Area (optional)</Label>
              <Input
                value={captureArea}
                onChange={(e) => setCaptureArea(e.target.value)}
                placeholder="e.g. Kitchen, Roof, Basement"
                style={{ marginTop: "0.375rem" }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCaptureClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadAll}
              disabled={isUploading || pendingPhotos.length === 0}
            >
              {isUploading ? (
                <>
                  <Loader2
                    style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }}
                    className="animate-spin"
                  />
                  Uploading…
                </>
              ) : (
                <>
                  <Camera
                    style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }}
                  />
                  Save{" "}
                  {pendingPhotos.length > 0
                    ? `${pendingPhotos.length} Photo${pendingPhotos.length > 1 ? "s" : ""}`
                    : "Photos"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
